import React, { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "qrcode.react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Switch from "@material-ui/core/Switch";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import EqualizerOutlinedIcon from "@material-ui/icons/EqualizerOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import copyToClipboard from "../../utils/copyToClipboard";

/**
 * Formulários públicos de captação.
 *
 * Cada parceiro recebe um link próprio — que vira QR Code em campanha — e o
 * lead entra já com a tag daquele parceiro aplicada, sem depender de alguém
 * marcar depois.
 */
const useStyles = makeStyles((theme) => ({
  main: { padding: theme.spacing(2), height: "100%", overflowY: "auto" },
  linkBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  campo: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  codeArea: {
    "& textarea": {
      fontFamily: "monospace",
      fontSize: 12,
    },
  },
  aviso: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    padding: theme.spacing(1.5),
    borderRadius: 6,
    marginBottom: theme.spacing(2),
  },
  qrWrap: { textAlign: "center", padding: theme.spacing(2) },
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: theme.spacing(1.5),
  },
  kpi: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  kpiValor: { fontSize: 26, fontWeight: 700, lineHeight: 1.2 },
  barra: {
    display: "flex",
    height: 8,
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: theme.palette.divider,
    marginTop: theme.spacing(0.5),
  },
}));

const TIPOS = [
  { value: "text", label: "Texto" },
  { value: "email", label: "E-mail" },
  { value: "phone", label: "Telefone / WhatsApp" },
  { value: "number", label: "Número" },
  { value: "date", label: "Data" },
  { value: "textarea", label: "Texto longo" },
  { value: "select", label: "Lista suspensa" },
  { value: "radio", label: "Escolha única" },
  { value: "checkbox", label: "Caixa de marcação" },
];

const FORM_VAZIO = {
  name: "",
  description: "",
  status: "draft",
  visibility: "public",
  layout: "default",
  multiStep: false,
  challengeEnabled: false,
  submitLabel: "Enviar",
  successMessage: "",
  redirectUrl: "",
  tagIds: [],
  sourceId: "",
  maxSubmissionsPerHour: 10,
  accessPassword: "",
  customHtml: "",
  customCss: "",
  customJs: "",
  fields: [],
};

const chaveDoRotulo = (rotulo, usadas) => {
  const base =
    (rotulo || "campo")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || "campo";

  let chave = base;
  let i = 2;
  while (usadas.includes(chave)) {
    chave = `${base}_${i}`;
    i += 1;
  }
  return chave;
};

const Forms = () => {
  const classes = useStyles();

  const [forms, setForms] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [tags, setTags] = useState([]);
  const [sources, setSources] = useState([]);

  const [editor, setEditor] = useState(null);
  const [aba, setAba] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [confirmar, setConfirmar] = useState(null);
  const [qr, setQr] = useState(null);
  const [leads, setLeads] = useState(null);
  const [stats, setStats] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/forms");
      setForms(data);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    (async () => {
      try {
        const [m, t, s] = await Promise.all([
          api.get("/forms/mappings"),
          api.get("/client-tags"),
          api.get("/client-sources"),
        ]);
        setMappings(m.data || []);
        setTags(Array.isArray(t.data) ? t.data : t.data?.tags || []);
        setSources(Array.isArray(s.data) ? s.data : s.data?.sources || []);
      } catch (err) {
        // Listas auxiliares: a tela continua utilizável sem elas.
      }
    })();
  }, []);

  const urlPublica = (slug) => `${window.location.origin}/f/${slug}`;

  const abrirNovo = () => {
    setEditor({ ...FORM_VAZIO });
    setAba(0);
  };

  const abrirEdicao = async (row) => {
    try {
      const { data } = await api.get(`/forms/${row.id}`);
      setEditor({
        ...FORM_VAZIO,
        ...data,
        sourceId: data.sourceId || "",
        tagIds: data.tagIds || [],
        accessPassword: "",
        fields: (data.fields || []).map((f) => ({ ...f })),
      });
      setAba(0);
    } catch (err) {
      toastError(err);
    }
  };

  const mudar = (campo, valor) =>
    setEditor((atual) => ({ ...atual, [campo]: valor }));

  const addCampo = () => {
    setEditor((atual) => {
      const usadas = atual.fields.map((f) => f.key);
      return {
        ...atual,
        fields: [
          ...atual.fields,
          {
            key: chaveDoRotulo("pergunta", usadas),
            label: "",
            type: "text",
            mapping: "custom",
            required: false,
            step: 1,
            position: atual.fields.length,
            options: null,
          },
        ],
      };
    });
  };

  const mudarCampo = (i, campo, valor) => {
    setEditor((atual) => {
      const fields = [...atual.fields];
      const alvo = { ...fields[i], [campo]: valor };

      // A chave sai do rótulo enquanto ninguém a editou à mão: quem monta o
      // formulário não deveria precisar pensar em nome técnico.
      if (campo === "label") {
        const usadas = fields.filter((_, j) => j !== i).map((f) => f.key);
        alvo.key = chaveDoRotulo(valor, usadas);
      }

      // O destino sugere o tipo — telefone vira campo de telefone, que é o
      // que aciona a normalização no servidor.
      if (campo === "mapping") {
        const spec = mappings.find((m) => m.target === valor);
        if (spec?.fieldType) alvo.type = spec.fieldType;
      }

      fields[i] = alvo;
      return { ...atual, fields };
    });
  };

  const moverCampo = (i, delta) => {
    setEditor((atual) => {
      const fields = [...atual.fields];
      const j = i + delta;
      if (j < 0 || j >= fields.length) return atual;
      [fields[i], fields[j]] = [fields[j], fields[i]];
      return { ...atual, fields: fields.map((f, k) => ({ ...f, position: k })) };
    });
  };

  const removerCampo = (i) =>
    setEditor((atual) => ({
      ...atual,
      fields: atual.fields.filter((_, j) => j !== i),
    }));

  const salvar = async () => {
    setSalvando(true);
    try {
      const corpo = {
        ...editor,
        sourceId: editor.sourceId || null,
        maxSubmissionsPerHour: Number(editor.maxSubmissionsPerHour) || 10,
        fields: editor.fields.map((f, i) => ({
          ...f,
          position: i,
          step: Number(f.step) || 1,
          options:
            ["select", "radio"].includes(f.type) && typeof f.options === "string"
              ? f.options.split("\n").map((o) => o.trim()).filter(Boolean)
              : f.options,
        })),
      };
      if (!corpo.accessPassword) delete corpo.accessPassword;

      if (editor.id) await api.put(`/forms/${editor.id}`, corpo);
      else await api.post("/forms", corpo);

      toast.success("Formulário salvo.");
      setEditor(null);
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    try {
      await api.delete(`/forms/${confirmar.id}`);
      toast.success("Formulário removido.");
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmar(null);
    }
  };

  const verEstatisticas = async (row) => {
    setStats({ form: row, carregando: true });
    try {
      const { data } = await api.get(`/forms/${row.id}/stats`);
      setStats({ form: row, ...data });
    } catch (err) {
      setStats(null);
      toastError(err);
    }
  };

  const verLeads = async (row) => {
    try {
      const { data } = await api.get(`/forms/${row.id}/submissions`);
      setLeads({ form: row, ...data });
    } catch (err) {
      toastError(err);
    }
  };

  const copiar = async (texto) => {
    if (await copyToClipboard(texto)) toast.success("Link copiado.");
    else toast.error("Não foi possível copiar. Selecione o link e copie à mão.");
  };

  const colunas = useMemo(
    () => [
      {
        key: "name",
        label: "Formulário",
        render: (row) => (
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              /f/{row.slug}
            </Typography>
          </Box>
        ),
      },
      {
        key: "status",
        label: "Situação",
        render: (row) => (
          <Box display="flex" style={{ gap: 4 }} flexWrap="wrap">
            <Chip
              size="small"
              label={
                { draft: "Rascunho", published: "Publicado", archived: "Arquivado" }[
                  row.status
                ] || row.status
              }
              color={row.status === "published" ? "primary" : "default"}
            />
            {row.hasPassword && <Chip size="small" label="Com senha" />}
            {row.layout === "custom" && <Chip size="small" label="HTML próprio" />}
          </Box>
        ),
      },
      {
        key: "submissionCount",
        label: "Leads",
        type: "numeric",
        render: (row) => row.submissionCount || 0,
      },
      {
        key: "acoes",
        label: "",
        type: "actions",
        render: (row) => (
          <Box display="flex" justifyContent="flex-end">
            <Tooltip title="Desempenho">
              <IconButton size="small" onClick={() => verEstatisticas(row)}>
                <EqualizerOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Leads recebidos">
              <IconButton size="small" onClick={() => verLeads(row)}>
                <ListAltOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Link e QR Code">
              <IconButton
                size="small"
                onClick={() => setQr(row)}
                disabled={row.status !== "published"}
              >
                <FileCopyOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => abrirEdicao(row)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton size="small" onClick={() => setConfirmar(row)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={classes.main}>
      <PageHeader
        title="Formulários"
        count={forms.length}
        subtitle="Páginas públicas de captação. Cada parceiro recebe um link próprio e o lead entra já com a tag dele aplicada."
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={abrirNovo}
          >
            Novo formulário
          </Button>
        }
      />

      <DataTable
        columns={colunas}
        rows={forms}
        loading={carregando}
        getRowKey={(row) => row.id}
        emptyTitle="Nenhum formulário ainda"
        emptyDescription="Crie um formulário, publique e distribua o link — ou o QR Code — para cada parceiro."
        emptyAction={
          <Button variant="contained" color="primary" onClick={abrirNovo}>
            Criar o primeiro
          </Button>
        }
      />

      {/* ---------- Construtor ---------- */}
      <Dialog
        open={!!editor}
        onClose={() => setEditor(null)}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>
          {editor?.id ? "Editar formulário" : "Novo formulário"}
        </DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={aba}
            onChange={(_, v) => setAba(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
          >
            <Tab label="Geral" />
            <Tab label={`Perguntas (${editor?.fields?.length || 0})`} />
            <Tab label="Publicação" />
            <Tab label="Aparência" />
          </Tabs>

          {editor && aba === 0 && (
            <Box mt={2}>
              <TextField
                fullWidth
                variant="outlined"
                label="Nome do formulário"
                helperText="Aparece no topo da página pública e vira o endereço do link."
                value={editor.name}
                onChange={(e) => mudar("name", e.target.value)}
              />
              <Box mt={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  label="Descrição"
                  value={editor.description || ""}
                  onChange={(e) => mudar("description", e.target.value)}
                />
              </Box>
              <Box mt={2} display="flex" style={{ gap: 16 }} flexWrap="wrap">
                <TextField
                  select
                  variant="outlined"
                  label="Origem do lead"
                  style={{ minWidth: 220 }}
                  helperText="Alimenta o retorno por canal."
                  value={editor.sourceId || ""}
                  onChange={(e) => mudar("sourceId", e.target.value)}
                >
                  <MenuItem value="">Nenhuma</MenuItem>
                  {sources.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  variant="outlined"
                  label="Tags aplicadas ao lead"
                  style={{ minWidth: 260 }}
                  helperText="É a atribuição do parceiro dono deste link."
                  SelectProps={{
                    multiple: true,
                    renderValue: (sel) =>
                      tags
                        .filter((t) => sel.includes(t.id))
                        .map((t) => t.name)
                        .join(", "),
                  }}
                  value={editor.tagIds || []}
                  onChange={(e) => mudar("tagIds", e.target.value)}
                >
                  {tags.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          )}

          {editor && aba === 1 && (
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editor.multiStep}
                    onChange={(e) => mudar("multiStep", e.target.checked)}
                    color="primary"
                  />
                }
                label="Mostrar uma etapa por vez"
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Formulário longo tem mais chance de ser concluído quando as
                perguntas chegam aos poucos.
              </Typography>

              <Box mt={2}>
                {(editor.fields || []).map((campo, i) => (
                  <Paper key={i} variant="outlined" className={classes.campo}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Pergunta"
                      style={{ flex: "2 1 220px" }}
                      value={campo.label}
                      onChange={(e) => mudarCampo(i, "label", e.target.value)}
                    />
                    <TextField
                      select
                      variant="outlined"
                      size="small"
                      label="Grava em"
                      style={{ flex: "1 1 200px" }}
                      value={campo.mapping}
                      onChange={(e) => mudarCampo(i, "mapping", e.target.value)}
                    >
                      {mappings.map((m) => (
                        <MenuItem key={m.target} value={m.target}>
                          {m.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      variant="outlined"
                      size="small"
                      label="Tipo"
                      style={{ flex: "1 1 150px" }}
                      value={campo.type}
                      onChange={(e) => mudarCampo(i, "type", e.target.value)}
                    >
                      {TIPOS.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {editor.multiStep && (
                      <TextField
                        type="number"
                        variant="outlined"
                        size="small"
                        label="Etapa"
                        style={{ width: 90 }}
                        value={campo.step || 1}
                        onChange={(e) => mudarCampo(i, "step", e.target.value)}
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!campo.required}
                          onChange={(e) =>
                            mudarCampo(i, "required", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Obrigatória"
                    />
                    {["select", "radio"].includes(campo.type) && (
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        variant="outlined"
                        size="small"
                        label="Alternativas (uma por linha)"
                        value={
                          Array.isArray(campo.options)
                            ? campo.options.join("\n")
                            : campo.options || ""
                        }
                        onChange={(e) => mudarCampo(i, "options", e.target.value)}
                      />
                    )}
                    <Box display="flex" alignItems="center">
                      <IconButton size="small" onClick={() => moverCampo(i, -1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moverCampo(i, 1)}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => removerCampo(i)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
                <Button startIcon={<AddIcon />} onClick={addCampo}>
                  Adicionar pergunta
                </Button>
              </Box>
            </Box>
          )}

          {editor && aba === 2 && (
            <Box mt={2}>
              <Box display="flex" style={{ gap: 16 }} flexWrap="wrap">
                <TextField
                  select
                  variant="outlined"
                  label="Situação"
                  style={{ minWidth: 200 }}
                  helperText="Só publicado responde no endereço público."
                  value={editor.status}
                  onChange={(e) => mudar("status", e.target.value)}
                >
                  <MenuItem value="draft">Rascunho</MenuItem>
                  <MenuItem value="published">Publicado</MenuItem>
                  <MenuItem value="archived">Arquivado</MenuItem>
                </TextField>
                <TextField
                  select
                  variant="outlined"
                  label="Acesso"
                  style={{ minWidth: 200 }}
                  value={editor.visibility}
                  onChange={(e) => mudar("visibility", e.target.value)}
                >
                  <MenuItem value="public">Qualquer pessoa com o link</MenuItem>
                  <MenuItem value="password">Exige senha de acesso</MenuItem>
                </TextField>
                {editor.visibility === "password" && (
                  <TextField
                    variant="outlined"
                    type="password"
                    label="Senha de acesso"
                    style={{ minWidth: 200 }}
                    helperText={
                      editor.hasPassword
                        ? "Preencha apenas para trocar a senha atual."
                        : "Quem abrir o link precisará informá-la."
                    }
                    value={editor.accessPassword || ""}
                    onChange={(e) => mudar("accessPassword", e.target.value)}
                  />
                )}
                <TextField
                  type="number"
                  variant="outlined"
                  label="Envios por hora, por pessoa"
                  style={{ minWidth: 200 }}
                  helperText="Barra enxurrada sem atrapalhar quem preenche."
                  value={editor.maxSubmissionsPerHour}
                  onChange={(e) =>
                    mudar("maxSubmissionsPerHour", e.target.value)
                  }
                />
              </Box>
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!editor.challengeEnabled}
                      onChange={(e) =>
                        mudar("challengeEnabled", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Pedir uma verificação antes de enviar"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  Uma conta simples ("Quanto é 7 + 5?") no fim do formulário.
                  Ligue apenas se este formulário estiver recebendo envios
                  automáticos — ela acrescenta um passo para todo mundo,
                  inclusive para quem escaneou o QR na sua frente.
                </Typography>
              </Box>

              <Box mt={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Mensagem após o envio"
                  value={editor.successMessage || ""}
                  onChange={(e) => mudar("successMessage", e.target.value)}
                />
              </Box>
              <Box mt={2} display="flex" style={{ gap: 16 }} flexWrap="wrap">
                <TextField
                  variant="outlined"
                  label="Redirecionar para (opcional)"
                  style={{ flex: "2 1 300px" }}
                  value={editor.redirectUrl || ""}
                  onChange={(e) => mudar("redirectUrl", e.target.value)}
                />
                <TextField
                  variant="outlined"
                  label="Texto do botão"
                  style={{ flex: "1 1 160px" }}
                  value={editor.submitLabel || ""}
                  onChange={(e) => mudar("submitLabel", e.target.value)}
                />
              </Box>
            </Box>
          )}

          {editor && aba === 3 && (
            <Box mt={2}>
              <TextField
                select
                variant="outlined"
                label="Aparência"
                style={{ minWidth: 260 }}
                value={editor.layout}
                onChange={(e) => mudar("layout", e.target.value)}
              >
                <MenuItem value="default">Padrão do CRM</MenuItem>
                <MenuItem value="custom">HTML, CSS e JavaScript próprios</MenuItem>
              </TextField>

              {editor.layout === "custom" && (
                <Box mt={2}>
                  <div className={classes.aviso}>
                    <Typography variant="body2">
                      O código roda isolado, numa janela sem acesso aos dados
                      nem à sessão do CRM. Para enviar, chame{" "}
                      <code>CRMForm.enviar(&#123; chave: valor &#125;)</code> —
                      as chaves são as das perguntas, disponíveis em{" "}
                      <code>CRMForm.campos</code>. Com a verificação ligada, ela
                      é pedida automaticamente no envio; para desenhar o próprio
                      campo, use <code>CRMForm.desafio.pergunta</code> e mande a
                      resposta em <code>__challengeAnswer</code>.
                    </Typography>
                  </div>
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    variant="outlined"
                    label="HTML"
                    className={classes.codeArea}
                    value={editor.customHtml || ""}
                    onChange={(e) => mudar("customHtml", e.target.value)}
                  />
                  <Box mt={2}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      variant="outlined"
                      label="CSS"
                      className={classes.codeArea}
                      value={editor.customCss || ""}
                      onChange={(e) => mudar("customCss", e.target.value)}
                    />
                  </Box>
                  <Box mt={2}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      variant="outlined"
                      label="JavaScript"
                      className={classes.codeArea}
                      value={editor.customJs || ""}
                      onChange={(e) => mudar("customJs", e.target.value)}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={salvar}
            disabled={salvando || !editor?.name || !editor?.fields?.length}
          >
            {salvando ? "Salvando…" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Link e QR ---------- */}
      <Dialog open={!!qr} onClose={() => setQr(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Link do formulário</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary">
            {qr?.name}
          </Typography>
          <div className={classes.linkBox}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={qr ? urlPublica(qr.slug) : ""}
              InputProps={{ readOnly: true }}
            />
            <IconButton onClick={() => copiar(urlPublica(qr.slug))} size="small">
              <FileCopyOutlinedIcon />
            </IconButton>
          </div>
          <div className={classes.qrWrap}>
            {qr && <QRCode value={urlPublica(qr.slug)} size={200} />}
            <Typography variant="caption" color="textSecondary" display="block">
              O cliente escaneia e o formulário abre no celular dele.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQr(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Desempenho ---------- */}
      <Dialog
        open={!!stats}
        onClose={() => setStats(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Desempenho de {stats?.form?.name}</DialogTitle>
        <DialogContent dividers>
          {stats?.carregando ? (
            <Typography variant="body2" color="textSecondary">
              Carregando…
            </Typography>
          ) : (
            <>
              <div className={classes.kpis}>
                <div className={classes.kpi}>
                  <div className={classes.kpiValor}>{stats?.visitors ?? 0}</div>
                  <Typography variant="caption" color="textSecondary">
                    pessoas abriram
                  </Typography>
                </div>
                <div className={classes.kpi}>
                  <div className={classes.kpiValor}>
                    {stats?.submissions ?? 0}
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    enviaram
                  </Typography>
                </div>
                <div className={classes.kpi}>
                  <div
                    className={classes.kpiValor}
                    style={{ color: "#0D9488" }}
                  >
                    {stats?.conversionRate ?? 0}%
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    conversão
                  </Typography>
                  {stats?.conversionUnreliable && (
                    <Tooltip title="Houve mais envios que visitantes identificados — várias pessoas atrás do mesmo IP contam como uma. Confie nos números absolutos.">
                      <Typography variant="caption" color="textSecondary" display="block">
                        aproximada
                      </Typography>
                    </Tooltip>
                  )}
                </div>
              </div>

              <Box mt={2.5}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Dos que enviaram
                </Typography>
                <div className={classes.barra}>
                  <div
                    style={{
                      width: `${
                        stats?.submissions
                          ? (stats.created / stats.submissions) * 100
                          : 0
                      }%`,
                      backgroundColor: "#2563EB",
                    }}
                  />
                  <div
                    style={{
                      width: `${
                        stats?.submissions
                          ? (stats.duplicates / stats.submissions) * 100
                          : 0
                      }%`,
                      backgroundColor: "#A16207",
                    }}
                  />
                </div>
                <Box mt={0.5} display="flex" style={{ gap: 16 }}>
                  <Typography variant="caption" color="textSecondary">
                    {stats?.created ?? 0} leads novos
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {stats?.duplicates ?? 0} já estavam na base
                  </Typography>
                </Box>
              </Box>

              <Box mt={2.5}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Envios barrados
                </Typography>
                {stats?.rejected ? (
                  <Box mt={0.5}>
                    {Object.entries(stats.rejectionsByReason || {}).map(
                      ([motivo, qtd]) => (
                        <Chip
                          key={motivo}
                          size="small"
                          style={{ marginRight: 6, marginBottom: 6 }}
                          label={`${
                            {
                              honeypot: "robô detectado",
                              rapido_demais: "envio instantâneo",
                              limite_por_ip: "excesso do mesmo IP",
                              sessao_expirada: "página velha demais",
                              validacao: "dados inválidos",
                            }[motivo] || motivo
                          }: ${qtd}`}
                        />
                      )
                    )}
                  </Box>
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    Nenhum até agora.
                  </Typography>
                )}
              </Box>

              <Box mt={2.5}>
                <Typography variant="caption" color="textSecondary">
                  {stats?.views ?? 0} aberturas no total — o mesmo visitante
                  recarregando a página conta uma vez em "pessoas abriram".
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStats(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Leads recebidos ---------- */}
      <Dialog
        open={!!leads}
        onClose={() => setLeads(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Leads de {leads?.form?.name}</DialogTitle>
        <DialogContent dividers>
          <DataTable
            columns={[
              {
                key: "cliente",
                label: "Lead",
                render: (row) =>
                  row.client
                    ? `${row.client.firstName || ""} ${row.client.lastName || ""}`.trim()
                    : "—",
              },
              {
                key: "contato",
                label: "Contato",
                render: (row) =>
                  row.client?.mobilePhone || row.client?.email || "—",
              },
              {
                key: "status",
                label: "Situação",
                render: (row) => (
                  <Chip
                    size="small"
                    label={
                      {
                        created: "Novo",
                        duplicate: "Já existia",
                        rejected: "Recusado",
                      }[row.status] || row.status
                    }
                  />
                ),
              },
              {
                key: "createdAt",
                label: "Recebido em",
                render: (row) =>
                  new Date(row.createdAt).toLocaleString("pt-BR"),
              },
            ]}
            rows={leads?.submissions || []}
            getRowKey={(row) => row.id}
            emptyTitle="Nenhum lead ainda"
            emptyDescription="Assim que alguém preencher, aparece aqui."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeads(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Excluir formulário"
        open={!!confirmar}
        onClose={() => setConfirmar(null)}
        onConfirm={excluir}
      >
        O link deixa de funcionar imediatamente. Os leads já recebidos
        permanecem no cadastro.
      </ConfirmationModal>
    </div>
  );
};

export default Forms;
